import { KeyRound, Loader2 } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { type AdminUserDetail, useAdminUser, useMe, useUpdateUser } from "@/lib/api";
import { apiErrorMessage, inlineFailure } from "@/lib/api-errors";
import { roleLabel } from "@/lib/roles";

import { extraRolesHeading, locksAccount, roleChoices, roleLock, roleLockText } from "./utils";

interface Props {
  userId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UserEditDialog({ userId, open, onOpenChange }: Props) {
  const { t } = useTranslation();
  const userQuery = useAdminUser(open ? userId : null);
  const user = userQuery.data;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("users.editTitle")}</DialogTitle>
        </DialogHeader>
        {userQuery.isError || userQuery.isLoading || !user ? (
          <>
            {userQuery.isError ? (
              <p className="text-destructive py-8 text-center text-sm">
                {t("users.detailLoadError")}
              </p>
            ) : (
              <div className="flex justify-center py-8">
                <Loader2
                  className="text-muted h-6 w-6 animate-spin"
                  aria-label={t("common.loading")}
                />
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                {t("common.cancel")}
              </Button>
              <Button disabled>{t("common.save")}</Button>
            </DialogFooter>
          </>
        ) : (
          // The key gives the form fresh state, initialised from the loaded user,
          // whenever another user or a newer version of this one (updated_at)
          // loads. Closing the dialog unmounts it, so reopening starts from the
          // current values too.
          <UserEditForm
            key={`${user.id}:${user.updated_at}`}
            user={user}
            onClose={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

interface UserEditFormProps {
  user: AdminUserDetail;
  onClose: () => void;
}

/**
 * The name and the role of one account. The role offers what the signed-in
 * person may give (`roleChoices`): first the roles every admin may give, then,
 * under a heading that says why, those they give only because of their own
 * role (an owner's or a developer's). It is locked, with a line that says why,
 * on their own account and on an account above their role. The latter locks
 * the name and Save too: only an owner or a developer changes an owner's
 * account, only a developer a developer's.
 */
function UserEditForm({ user, onClose }: UserEditFormProps) {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const updateMutation = useUpdateUser();
  const lock = roleLock(me, user);
  const lockText = roleLockText(lock);
  const locked = locksAccount(lock);
  const choices = roleChoices(me, user);
  const extraHeading = extraRolesHeading(me?.role);
  const lockTextId = useId();

  const [firstName, setFirstName] = useState(user.first_name ?? "");
  const [lastName, setLastName] = useState(user.last_name ?? "");
  const [role, setRole] = useState(user.role);
  // The failed attempt, worded at render so a language switch re-words it.
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);

  const handleSave = async () => {
    setFailure(null);
    try {
      await updateMutation.mutateAsync({ userId: user.id, firstName, lastName, role });
      onClose();
    } catch (err) {
      setFailure(inlineFailure({ error: err }));
    }
  };
  const error = failure && apiErrorMessage(failure.error, "updateUser");

  return (
    <>
      <div className="space-y-4">
        <p className="text-muted text-sm wrap-anywhere">{user.email}</p>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label htmlFor="editFirstName" className="text-foreground text-sm font-medium">
              {t("settings.firstName")}
            </label>
            <Input
              id="editFirstName"
              value={firstName}
              disabled={locked}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="editLastName" className="text-foreground text-sm font-medium">
              {t("settings.lastName")}
            </label>
            <Input
              id="editLastName"
              value={lastName}
              disabled={locked}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
        </div>
        <div className="space-y-2">
          <label htmlFor="editRole" className="text-foreground text-sm font-medium">
            {t("settings.role")}
          </label>
          <Select value={role} onValueChange={setRole} disabled={lock !== null}>
            <SelectTrigger
              id="editRole"
              className="w-full"
              aria-describedby={lockText ? lockTextId : undefined}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {choices.base.map((choice) => (
                <SelectItem key={choice} value={choice}>
                  {roleLabel(choice)}
                </SelectItem>
              ))}
              {choices.extra.length > 0 && (
                <>
                  {choices.base.length > 0 && <SelectSeparator />}
                  <SelectGroup>
                    <SelectLabel className="flex items-center gap-1.5">
                      <KeyRound className="text-accent size-3.5" aria-hidden />
                      {extraHeading}
                    </SelectLabel>
                    {choices.extra.map((choice) => (
                      <SelectItem key={choice} value={choice}>
                        {roleLabel(choice)}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </>
              )}
            </SelectContent>
          </Select>
          {lockText && (
            <p id={lockTextId} className="text-muted text-sm">
              {lockText}
            </p>
          )}
        </div>
        {error && <p className="text-destructive text-sm">{error}</p>}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          {t("common.cancel")}
        </Button>
        <Button onClick={handleSave} disabled={locked || updateMutation.isPending}>
          {updateMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            t("common.save")
          )}
        </Button>
      </DialogFooter>
    </>
  );
}
